import * as React from 'react'
import { Modal, ModalOverlay } from 'react-aria-components'
import { cn } from '../../lib/utils'

/**
 * Overlay — the stateless plumbing shared by every modal overlay (AlertDialog,
 * Dialog, and future modal pop-ups). `OverlayWrapper` owns the open/close state
 * and exposes it via context; `Backdrop` renders the dimmed backdrop + centered
 * modal container (react-aria's `ModalOverlay` + `Modal`) and traps/returns
 * focus. Compose them with a `Dialog`:
 *
 *   <OverlayWrapper trigger={<Button>Open</Button>}>
 *     <Backdrop>
 *       <Dialog>…</Dialog>
 *     </Backdrop>
 *   </OverlayWrapper>
 *
 * State is managed here (not react-aria's `DialogTrigger`) so the design system's
 * native `Button` (or any trigger that forwards `onClick`) opens it without being
 * a react-aria pressable — native buttons also handle Enter/Space for free.
 * Animations ride react-aria's `data-[entering]` / `data-[exiting]` states, so no
 * animation dependency is needed.
 */

interface OverlayContextValue {
  isOpen: boolean
  setOpen: (open: boolean) => void
  close: () => void
}

const OverlayContext = React.createContext<OverlayContextValue | null>(null)

/**
 * Shared backdrop-layer classes for every modal overlay (`Backdrop` and
 * `SheetOverlay`): the fixed full-screen dim + the fade in/out on react-aria's
 * entering/exiting states. Alignment (centered vs edge-anchored) and the inner
 * panel's own transition (zoom vs slide) are layered on by each consumer, but
 * the dim color and fade timing stay in one place so the surfaces can't drift.
 */
export const overlayBackdropClass = cn(
  'fixed inset-0 z-50 flex bg-black/40',
  'transition-opacity duration-200 ease-out',
  'data-[entering]:opacity-0 data-[exiting]:opacity-0'
)

/** Read the enclosing overlay's open state + `close()`. */
export const useOverlay = (): OverlayContextValue => {
  const ctx = React.useContext(OverlayContext)
  if (!ctx) {
    throw new Error('useOverlay must be used within an <OverlayWrapper>')
  }
  return ctx
}

export interface OverlayWrapperProps {
  /** Overlay content — typically a `<Backdrop>` wrapping a `<Dialog>`. */
  children: React.ReactNode
  /**
   * Element that opens the overlay; its `onClick` is wired through, plus
   * `aria-haspopup`/`aria-expanded`. Must spread unknown props onto a real DOM
   * element (the design system's `Button` does) or those ARIA attributes are
   * dropped and assistive tech won't announce the dialog relationship.
   */
  trigger?: React.ReactElement
  /** Controlled open state. */
  isOpen?: boolean
  /** Uncontrolled initial open state. */
  defaultOpen?: boolean
  /** Called when the open state changes. */
  onOpenChange?: (isOpen: boolean) => void
}

export const OverlayWrapper = ({
  children,
  trigger,
  isOpen,
  defaultOpen,
  onOpenChange,
}: OverlayWrapperProps) => {
  const isControlled = isOpen !== undefined
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false)
  const open = isControlled ? isOpen : internalOpen

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next)
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange]
  )
  const close = React.useCallback(() => setOpen(false), [setOpen])

  const ctx = React.useMemo(
    () => ({ isOpen: open, setOpen, close }),
    [open, setOpen, close]
  )

  const wiredTrigger =
    trigger &&
    React.cloneElement(
      trigger as React.ReactElement<Record<string, unknown>>,
      {
        'aria-haspopup': 'dialog',
        'aria-expanded': open,
        onClick: (e: React.MouseEvent) => {
          ;(
            trigger.props as { onClick?: (e: React.MouseEvent) => void }
          ).onClick?.(e)
          setOpen(true)
        },
      }
    )

  return (
    <OverlayContext.Provider value={ctx}>
      {wiredTrigger}
      {children}
    </OverlayContext.Provider>
  )
}
OverlayWrapper.displayName = 'OverlayWrapper'

export interface BackdropProps {
  /** Overlay panel — a `<Dialog>`. A function form receives `{ close }`. */
  children:
    | React.ReactNode
    | ((opts: { close: () => void }) => React.ReactNode)
  /** Close on backdrop click (default true). Set false for alert dialogs. */
  isDismissable?: boolean
  /** Disable closing via the Escape key (default false). */
  isKeyboardDismissDisabled?: boolean
  /** Classes for the modal container (e.g. a wider `max-w-*`). */
  className?: string
  /** Classes for the backdrop layer. */
  overlayClassName?: string
}

export const Backdrop = ({
  children,
  isDismissable = true,
  isKeyboardDismissDisabled = false,
  className,
  overlayClassName,
}: BackdropProps) => {
  const { isOpen, setOpen, close } = useOverlay()
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={setOpen}
      isDismissable={isDismissable}
      isKeyboardDismissDisabled={isKeyboardDismissDisabled}
      className={cn(
        overlayBackdropClass,
        'items-center justify-center p-4',
        overlayClassName
      )}
    >
      <Modal
        className={cn(
          'w-full max-w-md transition duration-200 ease-out',
          'data-[entering]:scale-95 data-[entering]:opacity-0',
          'data-[exiting]:scale-95 data-[exiting]:opacity-0',
          className
        )}
      >
        {typeof children === 'function' ? children({ close }) : children}
      </Modal>
    </ModalOverlay>
  )
}
Backdrop.displayName = 'Backdrop'
